import { Injectable, OnDestroy } from '@angular/core';
import { AuthService, IState, StateService } from 'src/app/services';
import { Role } from '../../shared/models/role.model';
import { Subscription } from 'rxjs';
import { CompanyType } from 'identity-webapi-client';

type HasAccessFunction = (authService: AuthService, stateService: StateService) => boolean;

export interface BadgeItem {
  type: string;
  value: string;
}

export interface Separator {
  name: string;
  type?: string;
}

export interface SubChildren {
  state: string;
  name: string;
  hasAccess?: HasAccessFunction;
  type?: string;
  beta?: boolean;
}

export interface ChildrenItems {
  state: string;
  name: string;
  type?: string;
  hasAccess?: HasAccessFunction;
  subchildren?: SubChildren[];
  beta?: boolean;
}

export interface Menu {
  state: string;
  name: string;
  type: string;
  icon: string;
  hasAccess?: HasAccessFunction;
  badge?: BadgeItem[];
  separator?: Separator[];
  children?: ChildrenItems[];
  beta?: boolean;
}

@Injectable()
export class MenuItems implements OnDestroy {

  menuItems: Menu[] = [];
  state: IState | undefined;

  private _subscriptions: Subscription[] = [];

  constructor(
    private authService: AuthService,
    private stateService: StateService
  ) {
    this.menuItems = MENUITEMS.filter(x => x.hasAccess == undefined);

    this._subscriptions.push(this.authService.userSubject.subscribe(() => {
      this.UpdateMenuItems();
    }));

    this._subscriptions.push(this.stateService.state$.subscribe((state) => {
      this.state = state;
      this.UpdateMenuItems();
    }));
  }

  ngOnDestroy(): void {
    if (this._subscriptions) {
      this._subscriptions.forEach(subscription => {
        if (subscription) {
          subscription.unsubscribe();
        }
      })
    }
  };

  private UpdateMenuItems() {
    let menuItems = MENUITEMS.filter(x => x.hasAccess == undefined || x.hasAccess(this.authService, this.stateService));
    menuItems.forEach(item => {
      item.children = item.children?.filter(x => x.hasAccess == undefined || x.hasAccess(this.authService, this.stateService));

      item.children?.forEach(childItem => {
        childItem.subchildren = childItem.subchildren?.filter(x => x.hasAccess == undefined || x.hasAccess(this.authService, this.stateService));
      });
    });

    this.menuItems = menuItems;
  }
}

const MENUITEMS: Menu[] = [
  {
    state: 'homes', // i.e dashboard
    name: 'Homes', // i.e Dashboard
    type: 'sub', // i.e link // sub, link, seprator
    icon: 'other_houses', // i.e av_timer
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isLoggedIn() && stateService.state?.company?.type !== CompanyType.Manufacturer; },
    children: [
      { state: 'dashboard', name: 'Dashboard', type: 'link' },
      { state: 'situview', name: 'situVIEW', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.SituView]); } },
      { state: 'situfacade', name: 'situFACADE', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.SituFacade]); } },
      { state: 'details', name: 'Details', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.BuilderAdmin, Role.Admin]); } },
      { state: 'upload', name: 'Upload IFC File', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasAnyRole([Role.BuilderAdmin, Role.Admin]) || authService.hasSituAccess(); } },
      { state: 'furnish', name: 'situFURNISH', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.SituFurnish]); } },
      { state: 'landscape', name: 'situSCAPE', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.SituLandscape]); } },
      { state: 'themes', name: 'Themes', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.Theme]) } },
      { state: 'demo', name: 'Demo', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess(); } },
      { state: 'placeholder-templates', name: 'Placeholder Templates', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.BuilderAdmin, Role.Admin]); } },
      { state: 'options', name: 'Options', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.BuilderAdmin, Role.Admin]); } },
      // {
      //   state: 'themes', name: 'Themes', type: 'subchild', hasAccess: (authService: AuthService, stateService: StateService) => {
      //     return authService.hasSituAccess();
      //   }, subchildren: [
      //     { state: 'imported-lines', name: 'Imported', type: 'link' }
      //   ]
      // }
    ]
  },
  {
    state: 'products', // i.e dashboard
    name: 'Products', // i.e Dashboard
    type: 'sub', // i.e link // sub, link, seprator
    icon: 'kitchen', // i.e av_timer
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isLoggedIn(); },
    children: [
      { state: 'dashboard', name: 'Dashboard', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || stateService.state?.company?.type === CompanyType.Manufacturer; } },
      { state: 'catalog', name: 'Catalog', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.User]); } },
      { state: 'add', name: 'Add Product', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin]); } },
      { state: 'brands', name: 'Brands', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin]); } },
      { state: 'ranges', name: 'Ranges', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin]); } },
      { state: 'decorate', name: 'situDECORATE', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasAnyRole([Role.Theme]); } },
      { state: 'categorization', name: 'Categorization', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess(); } },
      { state: 'marketing', name: 'Marketing', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.PartnerAdmin, Role.Admin]); } },
      { state: 'replacement-tags', name: 'Replacement Tags', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess(); } },
      { state: 'substitutions', name: 'Substitutions', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess(); } },
      { state: 'requested-products', name: 'Requested Products', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin, Role.BuilderAdmin, Role.CompanyProductPublisher]); } },
      { state: 'discover', name: 'Discover', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee]); } },
      {
        state: 'product', name: 'Legacy products', type: 'subchild', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.hasSituAccess();
        }, subchildren: [
          { state: 'add', name: 'Add Legacy Product', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess(); } },
        ]
      },
    ]
  },
  {
    state: 'situ-view', // i.e dashboard
    name: 'situVIEW', // i.e Dashboard
    type: 'link', // i.e link // sub, link, seprator
    icon: 'panorama', // i.e holiday_village
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasRole(Role.SituView); }
  },
  {
    state: 'situ-facade', // i.e dashboard
    name: 'situFACADE', // i.e Dashboard
    type: 'link', // i.e link // sub, link, seprator
    icon: 'image', // i.e holiday_village
    hasAccess: (authService: AuthService) => { return authService.hasSituAccess() || authService.hasRole(Role.SituFacade); }
  },
  {
    state: 'situ-daydream',
    name: 'situDAYDREAM',
    type: 'link',
    icon: 'auto_awesome',
    beta: true,
    hasAccess: (authService: AuthService) => { return authService.hasSituAccess() || authService.hasRole(Role.SituDaydream); }
  },
  {
    state: 'situ-vanity', // i.e dashboard
    name: 'situVANITY', // i.e Dashboard
    type: 'link', // i.e link // sub, link, seprator
    icon: 'design_services', // i.e av_timer
    hasAccess: (authService: AuthService) => {
      return authService.hasSituAccess() || authService.hasRole(Role.SituVanity);
    }
  },
  {
    state: 'situ-connect', // i.e dashboard
    name: 'situCONNECT', // i.e Dashboard
    type: 'link', // i.e link // sub, link, seprator
    icon: 'hub', // i.e holiday_village
    beta: true,
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.hasRole(Role.SituConnect); }
  },
  {
    state: 'environments', // i.e dashboard
    name: 'Environments', // i.e Dashboard
    type: 'link', // i.e link // sub, link, seprator
    icon: 'approval', // i.e av_timer
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); }
  },

  {
    state: 'reports', // i.e dashboard
    name: 'Reports', // i.e Dashboard
    type: 'sub', // i.e link // sub, link, seprator
    icon: 'assessment', // i.e av_timer
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isLoggedIn(); },
    children: [
      { state: 'cached-homes', name: 'Cached Homes', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin]) && stateService.state?.company?.type !== CompanyType.Manufacturer; } },
      { state: 'ifc-report', name: 'IFC File Feed', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return stateService.state?.company?.type !== CompanyType.Manufacturer; } },
      { state: 'platform-usage', name: 'Platform Usage', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess(); } },
      { state: 'product-impressions-data', name: 'Impressions Data', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } },
      { state: 'product-impressions-graph', name: 'Impressions Graph', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.Admin]) } },
      { state: 'monthly-billing-summary', name: 'Impressions Monthly Billing Summary', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper]); } },
      { state: 'render-summary-report', name: 'Render Summary', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper]); } },
      { state: 'builder-representation-report', name: 'Builder Representation', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper]); } },
      { state: 'supplier-dashboard', name: 'Supplier Dashboard (mockup)', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper]); } },
      { state: 'product-details', name: 'Product Details', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } },
      { state: 'theme-product-progress', name: 'Theme and Product Progress', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } },
      { state: 'view-home-analytics', name: 'Usage Report', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin]) && stateService.state?.company?.type !== CompanyType.Manufacturer; } },
      // { state: 'facade-report', name: 'Facade Report', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } }
    ]
  },
  {
    state: 'admin', // i.e dashboard
    name: 'Admin', // i.e Dashboard
    type: 'sub', // i.e link // sub, link, seprator
    icon: 'admin_panel_settings', // i.e av_timer
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release, Role.PartnerAdmin ]); },
    children: [
      { state: 'artifacts', name: 'Artifacts', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } },
      { state: 'companies', name: 'Companies', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } },
      { state: 'suppliers', name: 'Requested Suppliers', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } },
      { state: 'report-subscriptions', name: 'Report Subscriptions', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper]); } },
      { state: 'environments', name: 'Overrides', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasFullAccess(); } },
      {
        state: 'ifc-config', name: 'IFC Configuration', type: 'subchild', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin]) && stateService.state?.company?.type !== CompanyType.Manufacturer;
        }, subchildren: [
          { state: 'rooms', name: 'Rooms', type: 'link' },
          { state: 'building-materials', name: 'Building Materials', type: 'link' },
          { state: 'storeys', name: 'Storeys', type: 'link' },
          { state: 'furnishings', name: 'Furnishings', type: 'link' },
        ]
      },
      // {
      //   state: 'priority-homes', name: 'Priority Homes', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
      //     return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin]);
      //   }
      // },
      {
        state: 'partnerships', name: 'Partnerships', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.hasSituAccess() || authService.isAuthorized([Role.PartnerAdmin]);
        }
      },
      { state: 'release', name: 'Releases', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.isAuthorized([Role.Release]); }},
      { state: 'release-issues', name: 'Release Issues', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasSituAccess() || authService.isAuthorized([Role.Admin]) || authService.isAuthorized([Role.Release]); } },
      {
        state: 'rendered-output', name: 'Rendered Outputs', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.hasSituAccess();
        }
      },
      {
        state: 'situ-reels', name: 'SituReels', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.hasSituAccess() || authService.hasAnyRole([Role.BuilderAdmin, Role.Admin]);
        }
      },
      {
        state: 'recipe-event-summary', name: 'Recipe Event Summaries', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.hasSituAccess();
        }
      },
      {
        state: 'renderers', name: 'Renderers', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsDeveloper]);
        }
      },
      {
        state: 'renderer-pools', name: 'Renderer Pools', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsDeveloper, Role.SituSystemsAdmin]);
        }
      },
      {
        state: 'queues', name: 'Queues', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsDeveloper]);
        }
      },
      {
        state: 'tags', name: 'Tags', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsAdmin]);
        }
      },
      {
        state: 'user', name: 'Users', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin]);
        }
      },
      {
        state: 'situapi', name: 'situAPI', type: 'subchild',
        hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsDeveloper, Role.SituSystemsAdmin, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.PartnerAdmin]);
        },
        subchildren: [
          {
            state: 'manage-clients', name: 'Manage Clients', type: 'link',
            hasAccess: (authService: AuthService, stateService: StateService) => {
              return authService.isAuthorized([Role.SituSystemsDeveloper, Role.SituSystemsAdmin, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.PartnerAdmin]);
            }
          }
        ]
      },
      {
        state: 'styles', name: 'Style Categories', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release]) && stateService.state?.company?.type !== CompanyType.Manufacturer;
        }
      },
      {
        state: 'style-template', name: 'Style Templates', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.Release]) && stateService.state?.company?.type !== CompanyType.Manufacturer;
        }
      },
      {
        state: 'accessory-bundle', name: 'Accessory Bundles', type: 'link', beta: true, hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee]) && stateService.state?.company?.type !== CompanyType.Manufacturer;
        }
      },
      {
        state: 'file-vault', name: 'File Vault', type: 'link', hasAccess: (authService: AuthService, stateService: StateService) => {
          return authService.isAuthorized([Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee, Role.BuilderAdmin, Role.Admin, Role.CompanyProductPublisher]);
        }
      },
    ]
  },
  {
    state: 'docs', // i.e dashboard
    name: 'Docs', // i.e Dashboard
    type: 'sub', // i.e link // sub, link, seprator
    icon: 'info', // i.e av_timer,
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.isLoggedIn(); },
    children: [
      { name: 'situVIEW', type: 'link', state: 'situview' },
      { name: 'situFACADE', type: 'externalLink', state: 'https://docs.situsystems.com/reference/situfacade_createsitufacade' },
      { name: 'situRENDER', type: 'externalLink', state: 'https://docs.situsystems.com/reference/siturender_createsiturender' },
      { name: 'situPLAN', type: 'externalLink', state: 'https://docs.situsystems.com/reference/situplan_createsituplan' },
      { name: 'situVR', type: 'externalLink', state: 'https://docs.situsystems.com/reference/situvr_createsituvr' },
      { name: 'situPARAMETRIC', type: 'externalLink', state: 'https://docs.situsystems.com/reference/vanity_createsituparametricvanity' },
      { name: 'Webhook', type: 'externalLink', state: 'https://docs.situsystems.com/reference/webhook_createwebhook' },
      {
        state: 'modelling', name: 'Modelling', type: 'subchild',
        hasAccess: (authService: AuthService) => {
          return authService.isLoggedIn();
        },
        subchildren: [
          { name: 'Artwork', type: 'externalLink', state: 'https://doc.clickup.com/18617051/p/h/hr4pv-36287/9317cb870f30e83' },
          { name: 'Situ Product Handbook', type: 'externalLink', state: 'https://doc.clickup.com/18617051/d/h/hr4pv-40682/463eb542c289342' },
        ]
      }
    ]
  },
  {
    state: 'devs', // i.e dashboard
    name: 'Devs', // i.e Dashboard
    type: 'sub', // i.e link // sub, link, seprator
    icon: 'developer_board', // i.e av_timer
    hasAccess: (authService: AuthService, stateService: StateService) => { return authService.hasDeveloperAccess(); },
    children: [
      { state: 'address-test', name: 'Address Test', type: 'link' },
      { state: 'banner-test', name: 'Banner Test', type: 'link' },
      { state: 'landing-page-test', name: 'Landing Page Test', type: 'link' },
      { state: 'legal-agreement', name: 'Legal Agreement Test', type: 'link' },
      { state: 'pdf-viewer-test', name: 'PDF Viewer Test', type: 'link' },
      { state: 'style-guide', name: 'Style Guide', type: 'link' }
    ]
  }
];
